import { ConnectButton } from "thirdweb/react"
import { useQueryClient } from "@tanstack/react-query"
import { client } from "../../lib/client"
import type { LoginPayload, VerifyLoginPayloadParams } from "thirdweb/auth"
import {
  inAppWallet,
  createWallet,
} from "thirdweb/wallets";
import { baseSepolia } from "thirdweb/chains"
import { get, post } from "../../lib/api"

const wallets = [
  inAppWallet({
    auth: {
      options: ["google", "x", "discord"],
    },
  }),
  createWallet("io.metamask"),
];

export default function ConnectButtonAuth() {
  const queryClient = useQueryClient();
  
  return (
    <ConnectButton
      theme="dark"
      client={client}
      wallets={wallets}
      showAllWallets={false}
      connectModal={{ showThirdwebBranding: false, size: "compact" }}
      autoConnect={true}
      /**
       * Looking to authenticate with account abstraction enabled? Uncomment the following lines:
       *
       * accountAbstraction={{
       *  chain: baseSepolia,
       *  factoryAddress: "0x5cA3b8E5B82D826aF6E8e9BA9E4E8f95cbC177F4",
       *  gasless: true,
       * }}
       */
      auth={{
        /**
         * 	`getLoginPayload` should @return {VerifyLoginPayloadParams} object.
         * 	This can be generated on the server with the generatePayload method.
         */
        getLoginPayload: async (params: {
          address: string;
        }): Promise<LoginPayload> => {
          const payload = await get({
            url: `${import.meta.env.VITE_API_BASE_URL}/omniverse/user/login`,
            params: {
              address: params.address,
              chainId: baseSepolia.id.toString(),
            },
          });
          return payload;
        },
        /**
         * 	`doLogin` performs any logic necessary to log the user in using the signed payload.
         * 	In this case, this means sending the payload to the server for it to set a JWT cookie for the user.
         */
        doLogin: async (params: VerifyLoginPayloadParams) => {
          const loginResponse = await post({
            url: `${import.meta.env.VITE_API_BASE_URL}/omniverse/user/login`,
            params,
          });
          
          // Invalidate auth queries to refresh login state across the app
          queryClient.invalidateQueries({ queryKey: ['auth'] });
          
          // Handle redirect URL if present in the response
          if (loginResponse.success && loginResponse.redirectTo) {
            window.location.href = loginResponse.redirectTo;
          }
        },
        /**
         * 	`isLoggedIn` returns true or false to signal if the user is logged in.
         * 	Here, this is done by calling the server to check if the user has a valid JWT cookie set.
         */
        isLoggedIn: async () => {
          try {
            const isLogged = await get({
              url: `${import.meta.env.VITE_API_BASE_URL}/omniverse/user/isLoggedIn`,
            });
            
            // Handle both boolean response and object with success property
            if (typeof isLogged === 'boolean') {
              return isLogged;
            } else if (typeof isLogged === 'object' && 'success' in isLogged) {
              return isLogged.success ?? false;
            }
            return false;
          } catch (error) {
            console.error('[ConnectButtonAuth] isLoggedIn check failed:', error);
            return false;
          }
        },
        /**
         * 	`doLogout` performs any logic necessary to log the user out.
         * 	In this case, this means sending a request to the server to clear the JWT cookie.
         */
        doLogout: async () => {
          const loggedout = await post({
            url: `${import.meta.env.VITE_API_BASE_URL}/omniverse/user/logout`,
          });
          
          // Invalidate auth queries to refresh login state across the app
          queryClient.invalidateQueries({ queryKey: ['auth'] });

          if (loggedout.success && loggedout.redirectTo) {
            window.location.href = loggedout.redirectTo;
          }
        },
      }}
    />
  );
}
